import React, { useState } from 'react';
import Container from '../components/layout/Container';
import { useLanguage } from '../contexts/LanguageContext';
import { FaFacebook, FaInstagram, FaLinkedin } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import emailjs from '@emailjs/browser';
import './Contact.css';

// Schema validation
const contactSchema = z.object({
    name: z.string()
        .min(2, 'Name must be at least 2 characters')
        .max(50, 'Name must be less than 50 characters'),
    email: z.string()
        .email('Please enter a valid email address'),
    subject: z.string()
        .min(1, 'Subject is required')
        .max(100, 'Subject must be less than 100 characters'),
    message: z.string()
        .min(10, 'Message must be at least 10 characters')
        .max(1000, 'Message must be less than 1000 characters'),
});

const Contact = () => {
    const { t } = useLanguage();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({
        type: '',
        message: ''
    });

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(contactSchema),
        defaultValues: {
            name: '',
            email: '',
            subject: '',
            message: '',
        },
    });
    function getCurrentFormattedTime() {
        const now = new Date();
        // Format: 2024/05/24 15:30:45
        const formattedTime = `${now.getFullYear()}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getDate().toString().padStart(2, '0')} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
        return formattedTime;
    }

    const onSubmit = async (data) => {
        console.log(data);
        setIsSubmitting(true);
        setSubmitStatus({ type: '', message: '' });

        const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
        const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
        const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

        if (!serviceId || !templateId || !publicKey) {
            setSubmitStatus({
                type: 'error',
                message: 'Email service configuration missing. Please check your setup.',
            });
            setIsSubmitting(false);
            return;
        }

        try {
            await emailjs.send(
                serviceId,
                templateId,
                {
                    name: data.name,
                    email: data.email,
                    subject: data.subject,
                    time: getCurrentFormattedTime(),
                    message: data.message,
                    to_name: 'Da Vinci Institute', // Customize as needed
                },
                publicKey
            );

            setSubmitStatus({
                type: 'success',
                message: 'Message sent successfully! We\'ll get back to you soon.',
            });
            reset();
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus({
                type: 'error',
                message: 'Failed to send message. Please try again later.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-page">
            {/* Hero Header */}
            <header className="page-header">
                <Container>
                    <span className="section-subtitle">
                        <span className="material-icons" style={{ fontSize: '14px' }}>mail</span>
                        CONTACT US
                    </span>
                    <h1>
                        Let's Start a <span className="text-primary">Conversation</span>
                    </h1>
                    <p className="text-muted mt-4" style={{ maxWidth: '600px', margin: '0 auto' }}>
                        Have a question or want to learn more about our programs? Our team is here to provide you with all the guidance you need.
                    </p>
                </Container>
            </header>

            {/* Contact Form & Info */}
            <section className="section">
                <Container>
                    <div className="contact-grid">
                        {/* Form */}
                        <div className="contact-form-wrapper reveal reveal--right">
                            <h3>Send Us a Message</h3>
                            <p className="text-muted mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>

                            {submitStatus.message && (
                                <div className={`contact-message ${submitStatus.type === 'success'
                                    ? 'contact-message--success'
                                    : 'contact-message--error'
                                    }`}>
                                    <span className="material-icons">
                                        {submitStatus.type === 'success' ? 'check_circle' : 'error'}
                                    </span>
                                    {submitStatus.message}
                                </div>
                            )}

                            <form onSubmit={handleSubmit(onSubmit)} className="contact-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="name">{t('contact.name')}</label>
                                        <input
                                            type="text"
                                            id="name"
                                            className={`form-input ${errors.name ? 'form-input-error' : ''}`}
                                            placeholder="Your full name"
                                            {...register('name')}
                                        />
                                        {errors.name && (
                                            <p className="form-error">{errors.name.message}</p>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="email">{t('contact.email')}</label>
                                        <input
                                            type="email"
                                            id="email"
                                            className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                                            placeholder="your@email.com"
                                            {...register('email')}
                                        />
                                        {errors.email && (
                                            <p className="form-error">{errors.email.message}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="subject">{t('contact.subject')}</label>
                                    <input
                                        type="text"
                                        id="subject"
                                        className={`form-input ${errors.subject ? 'form-input-error' : ''}`}
                                        placeholder="Subject of your message"
                                        {...register('subject')}
                                    />
                                    {errors.subject && (
                                        <p className="form-error">{errors.subject.message}</p>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="message">{t('contact.message')}</label>
                                    <textarea
                                        id="message"
                                        className={`form-input form-textarea ${errors.message ? 'form-input-error' : ''}`}
                                        placeholder="Write your message..."
                                        rows="6"
                                        {...register('message')}
                                    ></textarea>
                                    {errors.message && (
                                        <p className="form-error">{errors.message.message}</p>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    className={`form-submit ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="material-icons animate-spin" style={{ fontSize: '18px' }}>refresh</span>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-icons" style={{ fontSize: '18px' }}>send</span>
                                            {t('common.send')}
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>

                        {/* Info */}
                        <div className="contact-info-wrapper reveal reveal--left delay-200">
                            <div className="contact-info-card">
                                <h4>Contact Information</h4>
                                <p className="text-muted mb-8">Reach us through any of the channels below.</p>

                                <div className="contact-info-items">
                                    <div className="contact-info-item">
                                        <div className="contact-info-item__icon">
                                            <span className="material-icons">location_on</span>
                                        </div>
                                        <div>
                                            <h5 className="contact-info-item__title">Address</h5>
                                            <p className="contact-info-item__text">{t('contact.address')}</p>
                                        </div>
                                    </div>
                                    <div className="contact-info-item">
                                        <div className="contact-info-item__icon">
                                            <span className="material-icons">phone</span>
                                        </div>
                                        <div>
                                            <h5 className="contact-info-item__title">Phone</h5>
                                            <p className="contact-info-item__text">+964 750 335 8885</p>
                                        </div>
                                    </div>
                                    <div className="contact-info-item">
                                        <div className="contact-info-item__icon">
                                            <span className="material-icons">email</span>
                                        </div>
                                        <div>
                                            <h5 className="contact-info-item__title">Email</h5>
                                            <p className="contact-info-item__text">info@dvinst.com</p>
                                        </div>
                                    </div>
                                    <div className="contact-info-item">
                                        <div className="contact-info-item__icon">
                                            <span className="material-icons">schedule</span>
                                        </div>
                                        <div>
                                            <h5 className="contact-info-item__title">Working Hours</h5>
                                            <p className="contact-info-item__text">Sun – Thu: 8:30 AM – 8:30 PM</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="contact-socials">
                                    <h5>Follow Us</h5>
                                    <div className="contact-socials__links">
                                        <a href="https://www.facebook.com/davinciinst" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FaFacebook /></a>
                                        <a href="https://www.instagram.com/davinci_institute/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FaInstagram /></a>
                                        <a href="https://www.linkedin.com/company/da-vinci-institute/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedin /></a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Map */}
                        <div className="contact-map">
                            <iframe
                                title="Da Vinci Institute Location"
                                src="https://www.google.com/maps/embed/v1/place?q=Da+Vinci+Institute,+Duhok,+Iraq&key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"
                                width="100%"
                                height="250"
                                style={{ border: 0, borderRadius: 'var(--radius-xl)' }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>
                        </div>
                    </div>
                </Container>
            </section>
        </div >
    );
};

export default Contact;
